<template>
  <el-dialog
    :title="currentMode === 'login' ? 'Login' : 'Sign Up'"
    :visible="visible"
    width="400px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="true"
    :before-close="handleClose"
    @close="handleClose"
    center
  >
    <div class="auth-dialog-content">
      <div class="logo-container">
        <img src="@/assets/Knowit_logo.png" alt="Knowit Logo" style="height: 32px; margin-right: 8px;">
        <span class="brand-name">Knowit</span>
      </div>
      
      <!-- Login Form -->
      <div v-if="currentMode === 'login'">
        <el-form :model="loginForm" :rules="loginRules" ref="loginForm">
          <el-form-item prop="student_email">
            <el-input v-model="loginForm.student_email" placeholder="User email"></el-input>
          </el-form-item>
          <el-form-item prop="opt_code" style="width: 100%">
            <el-input v-model="loginForm.opt_code" auto-complete="off" placeholder="Please enter the code" type="text">
              <el-button slot="suffix" class="send_code" @click="sendCode" :disabled="countdown > 0">{{ countdown > 0 ? `${countdown}s` : 'Send Code' }}</el-button>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="submitLoginForm('loginForm')" style="width: 100%;" :loading="loginLoading">Login</el-button>
          </el-form-item>
        </el-form>
        <div class="auth-note">
          <span>Don't have an account? </span>
          <a href="#" class="auth-link" @click.prevent="switchToSignup">Sign Up</a>
          <div class="auth-extra">Sign up to access all features and save your progress.</div>
        </div>
      </div>

      <!-- Signup Form -->
      <div v-if="currentMode === 'signup'">
        <template v-if="signupStep === 'form'">
          <el-form :model="signupForm" :rules="signupRules" ref="signupForm">
            <el-form-item label="User Email" prop="user_email">
              <el-input v-model="signupForm.user_email" placeholder="Enter your email"></el-input>
            </el-form-item>
            <el-form-item label="User Name" prop="user_name">
              <el-input v-model="signupForm.user_name" placeholder="Enter your name"></el-input>
            </el-form-item>
            <el-form-item label="User Type" prop="user_type">
              <el-radio-group v-model="signupForm.user_type">
                <el-radio label="individual">Individual</el-radio>
                <el-radio label="institution">Institution</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item prop="agreeToTerms">
              <el-checkbox v-model="signupForm.agreeToTerms">
                I agree to the 
                <a href="/#/terms-of-service" target="_blank" class="terms-link">Terms of Service</a> 
                and 
                <a href="/#/privacy-policy" target="_blank" class="terms-link">Privacy Policy</a>
              </el-checkbox>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="submitSignupForm('signupForm')" style="width: 100%" :loading="signupLoading">Sign Up</el-button>
            </el-form-item>
          </el-form>
        </template>

        <template v-if="signupStep === 'otp'">
          <div class="otp-header">
            <el-button type="text" icon="el-icon-arrow-left" class="otp-back" @click="signupStep = 'form'">Back</el-button>
          </div>
          <div class="otp-instructions">An OTP code has been sent to your email. Please enter it below to complete registration.</div>
          <el-form :model="otpForm" :rules="otpRules" ref="otpForm">
            <el-form-item label="OTP Code" prop="otp_code">
              <el-input v-model="otpForm.otp_code" maxlength="6" placeholder="Enter OTP code"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="submitOtpForm('otpForm')" style="width: 100%" :loading="otpLoading">Verify & Register</el-button>
            </el-form-item>
          </el-form>
        </template>

        <div class="auth-note">
          <span>Already have an account? </span>
          <a href="#" class="auth-link" @click.prevent="switchToLogin">Login</a>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'AuthDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialMode: {
      type: String,
      default: 'login',
      validator: value => ['login', 'signup'].includes(value)
    }
  },
  data() {
    return {
      currentMode: 'login',
      signupStep: 'form',
      countdown: 0,
      loginLoading: false,
      signupLoading: false,
      otpLoading: false,
      
      // Login form data
      loginForm: {
        student_email: '',
        opt_code: ''
      },
      loginRules: {
        student_email: [
          { required: true, message: 'Please input username', trigger: 'blur' }
        ],
        opt_code: [
          { required: true, message: 'Please input opt code', trigger: 'blur' }
        ]
      },

      // Signup form data
      signupForm: {
        user_name: '',
        user_email: '',
        user_type: '',
        agreeToTerms: false
      },
      signupRules: {
        user_name: [
          { required: true, message: 'Please enter your name', trigger: 'blur' }
        ],
        user_type: [
          { required: true, message: 'Please select a user type', trigger: 'change' }
        ],
        user_email: [
          { required: true, message: 'Please enter your email', trigger: 'blur' },
          { type: 'email', message: 'Please enter a valid email', trigger: ['blur', 'change'] }
        ],
        agreeToTerms: [
          { 
            validator: (rule, value, callback) => {
              if (!value) {
                callback(new Error('You must agree to the Terms of Service and Privacy Policy'));
              } else {
                callback();
              }
            }, 
            trigger: 'change' 
          }
        ]
      },

      // OTP form data
      otpForm: {
        otp_code: ''
      },
      otpRules: {
        otp_code: [
          { required: true, message: 'Please enter the OTP code', trigger: 'blur' },
          { min: 4, max: 6, message: 'OTP code should be 4-6 digits', trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentMode = this.initialMode
        this.resetForms()
      } else {
        // Dialog is closing, reset state
        this.resetForms()
        this.currentMode = this.initialMode
      }
    }
  },
  methods: {
    switchToSignup() {
      this.currentMode = 'signup'
      this.signupStep = 'form'
      this.resetForms()
    },
    
    switchToLogin() {
      this.currentMode = 'login'
      this.resetForms()
    },
    
    resetForms() {
      this.loginForm = { student_email: '', opt_code: '' }
      this.signupForm = { user_name: '', user_email: '', user_type: '', agreeToTerms: false }
      this.otpForm = { otp_code: '' }
      this.signupStep = 'form'
      this.countdown = 0
    },
    
    startCountdown() {
      this.countdown = 30;
      const timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    },

    sendCode() {
      if (this.loginForm.student_email == "") {
        this.$message.error("Please enter your email");
        return;
      }
      this.$store.dispatch('createLoginOPT', this.loginForm.student_email).then((res) => {
        console.log(res)
        if (res == null) {
          this.$message.success('OTP Sent');
          this.startCountdown();
        } else {
          this.$message.success(res);
          this.startCountdown();
        }
      })
    },

    submitLoginForm(formName) {
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.loginLoading = true;
          this.$store.dispatch('loginByEmail', {
            student_email: this.loginForm.student_email,
            opt_code: this.loginForm.opt_code
          }).then((res) => {
            this.loginLoading = false;
            if (res == null) {
              this.$message.success('Login successful')
              this.$emit('update:visible', false)
              this.$emit('login-success')
              window.location.reload()
            } else {
              this.$message.success(res)
            }
          }).catch((error) => {
            this.loginLoading = false;
            this.$message.error('Login failed')
            console.error('Login error:', error)
          })
        }
      })
    },

    async submitSignupForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.signupLoading = true;
          try {
            // 1. Verify email availability
            const resp = await postRequest('verifyEmailAvailability', {
              student_email: this.signupForm.user_email
            });
            
            if (resp.data.code !== 0) {
              this.$message.error('This email is already registered. Please directly login or change to another email.');
              return;
            } else {
              // Switch to OTP input interface
              this.signupStep = 'otp';
              this.$message.success('Email verified. Please check your email for OTP code.');
            }
          } catch (error) {
            this.$message.error('Failed to verify email')
            console.error('Email verification error:', error)
          } finally {
            this.signupLoading = false;
          }
        }
      });
    },

    async submitOtpForm(formName) {
      this.$refs[formName].validate(async (valid) => {
        if (valid) {
          this.otpLoading = true;
          try {
            // Call backend to verify OTP and complete registration
            const resp = await postRequest('signupByEmail', {
              student_email: this.signupForm.user_email,
              opt_code: this.otpForm.otp_code,
              user_name: this.signupForm.user_name,
              user_type: this.signupForm.user_type,
            });
            
            if (resp && resp.data && resp.data.code === 0) {
              this.$message.success('Sign up successful! You will automatically login.');

              this.$store.dispatch('loginByEmail', {
                student_email: this.signupForm.user_email,
                opt_code: this.otpForm.otp_code
              }).then((res) => {
                if (res == null) {
                  this.$message.success('Login successful')
                  this.$emit('update:visible', false)
                  this.$emit('login-success')
                  window.location.reload()
                } else {
                  this.$message.success(res)
                }
              })
            } else if (resp && resp.data && resp.data.code === -2) {
              this.$message.error('OTP error. Please check the code and try again.');
            } else {
              this.$message.error((resp && resp.data && resp.data.msg) || 'Registration failed. Please try again.');
            }
          } catch (error) {
            this.$message.error('Registration failed')
            console.error('Registration error:', error)
          } finally {
            this.otpLoading = false;
          }
        }
      });
    },

    handleClose(done) {
      this.$emit('update:visible', false)
      if (done) done()
    }
  }
}
</script>

<style scoped lang="scss">
:deep(.el-input__suffix) {
  right: 0;
}

.auth-dialog-content {
  padding: 20px 0;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.brand-name {
  font-size: 24px;
  font-weight: bold;
  color: #1159AF;
  letter-spacing: 1px;
}

.otp-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.otp-back {
  margin-left: -8px;
  color: #409EFF;
  font-size: 16px;
  font-weight: 500;
}

.otp-instructions {
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
  text-align: center;
}

.auth-note {
  text-align: center;
  margin-top: 18px;
  font-size: 14px;
  color: #666;
}

.auth-link {
  color: #1159AF;
  font-weight: 500;
  text-decoration: underline;
  margin-left: 3px;
  cursor: pointer;
}

.auth-link:hover {
  color: #409EFF;
}

.auth-extra {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.terms-link {
  color: #1159AF;
  text-decoration: underline;
  cursor: pointer;
}

.terms-link:hover {
  color: #409EFF;
}
</style> 